import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Box, Typography, IconButton, Grid, Stack, Switch, FormControlLabel,
  Divider, Alert, CircularProgress, Paper, Tooltip, Snackbar, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import PolicyIcon from '@mui/icons-material/Policy';
import {
  getLivePolicy, updateLivePolicy, applyPolicyTemplate
} from '../../services/livePolicyService';

const SectionTitle = ({ children }) => (
  <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1, mb: 1 }}>{children}</Typography>
);

const fmtVnd = (v) => new Intl.NumberFormat('vi-VN').format(Number(v) || 0) + 'đ';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN') : '';

// Tóm tắt những gì scheduledChange sẽ đổi, để admin thấy ngay mà không phải đọc JSON.
const describeScheduledChange = (sc) => {
  if (!sc) return [];
  const out = [];
  if (sc.gmvBonusTiers?.length) out.push(`Mốc thưởng GMV cá nhân (${sc.gmvBonusTiers.length} bậc, từ ${fmtVnd(sc.gmvBonusTiers[0].minGMV)})`);
  if (sc.tiktokGmvBonusTiers?.length) out.push(`Mốc thưởng GMV TikTok (${sc.tiktokGmvBonusTiers.length} bậc)`);
  if (sc.latePenalty) {
    out.push(sc.latePenalty.enabled
      ? `Chế tài trễ: BẬT — ca thường ${fmtVnd(sc.latePenalty.normalShift)} · ca sale/đêm ${fmtVnd(sc.latePenalty.saleNightShift)} · giờ phụ ${fmtVnd(sc.latePenalty.extraHours)}`
      : 'Chế tài trễ: TẮT');
  }
  if (sc.leader?.teamCommissionPercent != null) out.push(`Hoa hồng team Lead → ${sc.leader.teamCommissionPercent}%`);
  if (sc.minHoursPerMonth != null) out.push(`Giờ tối thiểu/tháng → ${sc.minHoursPerMonth}h`);
  if (sc.evenDistributionBonus != null) out.push(sc.evenDistributionBonus > 0
    ? `Thưởng đạt giờ đều các tuần → ${fmtVnd(sc.evenDistributionBonus)}`
    : 'Bỏ thưởng đạt giờ đều các tuần (0đ)');
  if (sc.shortfallPenaltyTiers?.length) out.push(`Chế tài thiếu giờ (${sc.shortfallPenaltyTiers.length} bậc)`);
  if (sc.attendanceBonuses) out.push('Thưởng chuyên cần');
  if (sc.nightShift) out.push('Ca đêm sale');
  return out;
};

// Mọi lần hẹn đổi chính sách (scheduledChange + scheduledChanges), xếp theo ngày hiệu lực
const listScheduledChanges = (p) => [p?.scheduledChange, ...(p?.scheduledChanges || [])]
  .filter(sc => sc?.effectiveFrom)
  .sort((a, b) => new Date(a.effectiveFrom) - new Date(b.effectiveFrom));

const LivePolicyEditor = ({ open, onClose, user, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDefault, setIsDefault] = useState(true);
  const [p, setP] = useState(null);
  const [noti, setNoti] = useState({ open: false, message: '', severity: 'success' });

  const show = (message, severity = 'success') => setNoti({ open: true, message, severity });
  const set = (field, value) => setP(prev => ({ ...prev, [field]: value }));
  const setNested = (group, field, value) => setP(prev => ({ ...prev, [group]: { ...prev[group], [field]: value } }));

  const fetchPolicy = useCallback(async () => {
    if (!user?._id) return;
    setLoading(true);
    const res = await getLivePolicy(user._id);
    if (res.success) {
      setP(res.data);
      setIsDefault(!!res.isDefault);
    } else {
      show(res.error?.message || 'Lỗi tải chính sách', 'error');
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { if (open) fetchPolicy(); }, [open, fetchPolicy]);

  const handleSave = async () => {
    setSaving(true);
    const res = await updateLivePolicy(user._id, p);
    if (res.success) { show('Đã lưu chính sách'); setIsDefault(false); onSaved?.(); }
    else show(res.error?.message || 'Lỗi lưu', 'error');
    setSaving(false);
  };

  const handleApplyTemplate = async (tmpl) => {
    if (!window.confirm(`Áp template "${tmpl}" sẽ ghi đè toàn bộ cấu hình hiện tại. Tiếp tục?`)) return;
    setSaving(true);
    const res = await applyPolicyTemplate(user._id, tmpl);
    if (res.success) { setP(res.data); setIsDefault(false); show(`Đã áp template ${tmpl}`); onSaved?.(); }
    else show(res.error?.message || 'Lỗi', 'error');
    setSaving(false);
  };

  // ----- Tier table helpers -----
  // null = "để trống có nghĩa" (∞ / không cộng luỹ tiến) — phải giữ nguyên null,
  // Number(null) ra 0 sẽ làm bậc không bao giờ khớp hoặc vi phạm ràng buộc min của schema.
  const updateTier = (key, idx, field, value) => {
    const v = value === null ? null : value === '' ? '' : Number(value);
    setP(prev => ({ ...prev, [key]: prev[key].map((t, i) => i === idx ? { ...t, [field]: v } : t) }));
  };
  const addTier = (key, template) => setP(prev => ({ ...prev, [key]: [...(prev[key] || []), { ...template }] }));
  const fmtTierRange = (t) =>
    `${fmtVnd(t.minGMV)}${t.maxGMV != null ? ` – ${fmtVnd(t.maxGMV)}` : ' trở lên'}`;
  const removeTier = (key, idx) => setP(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }));

  const renderGmvTiers = (key, label) => (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" fontWeight={600}>{label}</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => addTier(key, { minGMV: 0, maxGMV: null, bonus: 0 })} sx={{ textTransform: 'none' }}>Thêm bậc</Button>
      </Stack>
      {(p[key] || []).map((t, i) => (
        <Grid container spacing={1} alignItems="center" key={i} sx={{ mb: 0.5 }}>
          <Grid item xs={6} sm={2.4}><TextField fullWidth size="small" label="GMV từ" type="number" value={t.minGMV} onChange={e => updateTier(key, i, 'minGMV', e.target.value)} /></Grid>
          <Grid item xs={6} sm={2.4}><TextField fullWidth size="small" label="GMV đến (trống=∞)" type="number" value={t.maxGMV ?? ''} onChange={e => updateTier(key, i, 'maxGMV', e.target.value === '' ? null : e.target.value)} /></Grid>
          <Grid item xs={6} sm={2.4}><TextField fullWidth size="small" label="Thưởng (đ)" type="number" value={t.bonus} onChange={e => updateTier(key, i, 'bonus', e.target.value)} /></Grid>
          <Grid item xs={6} sm={2}>
            <Tooltip title="Cộng luỹ tiến: cứ mỗi bấy nhiêu GMV vượt quá mốc 'GMV từ' thì cộng thêm. Trống = không cộng.">
              <TextField fullWidth size="small" label="Mỗi (đ) vượt" type="number" value={t.stepGMV ?? ''} onChange={e => updateTier(key, i, 'stepGMV', e.target.value === '' ? null : e.target.value)} />
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Cộng thêm (đ)" type="number" value={t.stepBonus ?? ''} onChange={e => updateTier(key, i, 'stepBonus', e.target.value === '' ? null : e.target.value)} /></Grid>
          <Grid item xs={12} sm={0.8}><IconButton size="small" color="error" onClick={() => removeTier(key, i)}><DeleteIcon fontSize="small" /></IconButton></Grid>
        </Grid>
      ))}
      {(p[key] || []).filter(t => t.stepGMV > 0 && t.stepBonus > 0).map((t, i) => (
        <Typography key={`s${i}`} variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          Bậc {fmtTierRange(t)}: {fmtVnd(t.bonus)} + {fmtVnd(t.stepBonus)} cho mỗi {fmtVnd(t.stepGMV)} vượt mốc{' '}
          {fmtVnd(t.minGMV)} — vd {fmtVnd(t.minGMV + t.stepGMV)} → {fmtVnd(t.bonus + t.stepBonus)}.
        </Typography>
      ))}
    </Box>
  );

  if (!user) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PolicyIcon color="primary" /> Chính sách Live — {user.fullName}
          {isDefault && <Chip size="small" color="warning" label="Đang dùng default theo vai trò" />}
        </DialogTitle>
        <DialogContent dividers>
          {loading || !p ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={() => handleApplyTemplate('liver')}>Áp template LIVER</Button>
                <Button size="small" variant="outlined" onClick={() => handleApplyTemplate('lead')}>Áp template LEAD</Button>
                <FormControlLabel sx={{ ml: 'auto' }} control={<Switch checked={p.isActive !== false} onChange={e => set('isActive', e.target.checked)} />} label="Kích hoạt" />
              </Stack>

              {/* Lịch đổi chính sách — các số bên dưới là bản ĐANG áp, bộ mới tự có hiệu lực đúng ngày */}
              {listScheduledChanges(p).map((sc) => (
                <Alert key={String(sc.effectiveFrom)} severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Đã hẹn đổi chính sách từ {fmtDate(sc.effectiveFrom)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.75 }}>
                    Kỳ bắt đầu từ {fmtDate(sc.effectiveFrom)} trở đi sẽ tự áp các thay đổi dưới đây, kỳ trước đó giữ nguyên số cũ.
                  </Typography>
                  <Stack spacing={0.25}>
                    {describeScheduledChange(sc).map((line, i) => (
                      <Typography key={i} variant="caption" component="div">• {line}</Typography>
                    ))}
                  </Stack>
                </Alert>
              ))}

              {/* Giờ & gate lương */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <SectionTitle>Giờ làm & lương cứng</SectionTitle>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Giờ tối thiểu/tháng" type="number" value={p.minHoursPerMonth} onChange={e => set('minHoursPerMonth', Number(e.target.value))} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Trần giờ KPI/tuần (0=tắt)" type="number" value={p.weeklyKpiCapHours} onChange={e => set('weeklyKpiCapHours', Number(e.target.value))} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Giờ tối thiểu/tuần" type="number" value={p.weeklyMinHours} onChange={e => set('weeklyMinHours', Number(e.target.value))} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Số tuần cần đạt" type="number" value={p.requiredWeeks} onChange={e => set('requiredWeeks', Number(e.target.value))} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Thưởng đều đặn (đ)" type="number" value={p.evenDistributionBonus} onChange={e => set('evenDistributionBonus', Number(e.target.value))} /></Grid>
                  <Grid item xs={12} sm={8}><FormControlLabel control={<Switch checked={!!p.gateBaseSalaryOnMinHours} onChange={e => set('gateBaseSalaryOnMinHours', e.target.checked)} />} label="Không đủ giờ tối thiểu → không trả lương cứng" /></Grid>
                </Grid>
              </Paper>

              {/* Thưởng mốc GMV */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <SectionTitle>Thưởng mốc GMV</SectionTitle>
                {renderGmvTiers('gmvBonusTiers', 'Theo tổng GMV cá nhân')}
                <Divider sx={{ my: 1.5 }} />
                {renderGmvTiers('tiktokGmvBonusTiers', 'Theo GMV TikTok cá nhân (độc lập)')}
              </Paper>

              {/* Ca đêm */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <SectionTitle>Ca đêm sale</SectionTitle>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Bắt đầu" type="time" InputLabelProps={{ shrink: true }} value={p.nightShift?.startTime || '23:00'} onChange={e => setNested('nightShift', 'startTime', e.target.value)} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Kết thúc" type="time" InputLabelProps={{ shrink: true }} value={p.nightShift?.endTime || '01:00'} onChange={e => setNested('nightShift', 'endTime', e.target.value)} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Hệ số giờ" type="number" inputProps={{ step: 0.1 }} value={p.nightShift?.hourMultiplier ?? 1.5} onChange={e => setNested('nightShift', 'hourMultiplier', Number(e.target.value))} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Phụ cấp/ca (đ)" type="number" value={p.nightShift?.allowancePerShift ?? 150000} onChange={e => setNested('nightShift', 'allowancePerShift', Number(e.target.value))} /></Grid>
                </Grid>
              </Paper>

              {/* Chế tài thiếu giờ */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <SectionTitle>Chế tài thiếu giờ</SectionTitle>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => addTier('shortfallPenaltyTiers', { minShortH: 1, maxShortH: null, perHourPenalty: 0, cutAllBonuses: false, formalWarning: false })} sx={{ textTransform: 'none' }}>Thêm bậc</Button>
                </Stack>
                {(p.shortfallPenaltyTiers || []).map((t, i) => (
                  <Grid container spacing={1} alignItems="center" key={i} sx={{ mb: 0.5 }}>
                    <Grid item xs={6} sm={2.2}><TextField fullWidth size="small" label="Thiếu từ (h)" type="number" value={t.minShortH} onChange={e => updateTier('shortfallPenaltyTiers', i, 'minShortH', e.target.value)} /></Grid>
                    <Grid item xs={6} sm={2.2}><TextField fullWidth size="small" label="Đến (h, trống=∞)" type="number" value={t.maxShortH ?? ''} onChange={e => updateTier('shortfallPenaltyTiers', i, 'maxShortH', e.target.value === '' ? null : e.target.value)} /></Grid>
                    <Grid item xs={6} sm={2.6}><TextField fullWidth size="small" label="Phạt/giờ (đ)" type="number" value={t.perHourPenalty} onChange={e => updateTier('shortfallPenaltyTiers', i, 'perHourPenalty', e.target.value)} /></Grid>
                    <Grid item xs={6} sm={2}><FormControlLabel control={<Switch size="small" checked={!!t.cutAllBonuses} onChange={e => setP(prev => ({ ...prev, shortfallPenaltyTiers: prev.shortfallPenaltyTiers.map((x, j) => j === i ? { ...x, cutAllBonuses: e.target.checked } : x) }))} />} label="Cắt thưởng" />
                    </Grid>
                    <Grid item xs={6} sm={2}><FormControlLabel control={<Switch size="small" checked={!!t.formalWarning} onChange={e => setP(prev => ({ ...prev, shortfallPenaltyTiers: prev.shortfallPenaltyTiers.map((x, j) => j === i ? { ...x, formalWarning: e.target.checked } : x) }))} />} label="Cảnh cáo" /></Grid>
                    <Grid item xs={12} sm={1}><IconButton size="small" color="error" onClick={() => removeTier('shortfallPenaltyTiers', i)}><DeleteIcon fontSize="small" /></IconButton></Grid>
                  </Grid>
                ))}
              </Paper>

              {/* Chế tài đi live trễ */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <SectionTitle>Chế tài đi live trễ</SectionTitle>
                  <FormControlLabel
                    control={<Switch checked={!!p.latePenalty?.enabled} onChange={e => setNested('latePenalty', 'enabled', e.target.checked)} />}
                    label="Áp dụng"
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                  Tiền phạt tính tự động từ các lần trễ được ghi nhận ở mục &ldquo;Chế tài đi live trễ&rdquo; trên menu admin.
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Ca thường (đ/lần)" type="number" value={p.latePenalty?.normalShift ?? 0} onChange={e => setNested('latePenalty', 'normalShift', Number(e.target.value))} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Ca sale/đêm (đ/lần)" type="number" value={p.latePenalty?.saleNightShift ?? 0} onChange={e => setNested('latePenalty', 'saleNightShift', Number(e.target.value))} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Giờ phụ (đ/lần)" type="number" value={p.latePenalty?.extraHours ?? 0} onChange={e => setNested('latePenalty', 'extraHours', Number(e.target.value))} /></Grid>
                  <Grid item xs={6} sm={3}>
                    <Tooltip title="Phiên live bị trễ mất hoa hồng ưu đãi khung giờ, hạ về mức này. Trống = không hạ.">
                      <TextField fullWidth size="small" label="Hoa hồng phiên trễ (%)" type="number" inputProps={{ step: 0.5 }}
                        value={p.latePenalty?.downgradeCommissionRate ?? ''}
                        onChange={e => setNested('latePenalty', 'downgradeCommissionRate', e.target.value === '' ? null : Number(e.target.value))} />
                    </Tooltip>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" fontWeight={600}>Hệ số lũy tiến theo số lần trễ trong tháng</Typography>
                  <Button size="small" startIcon={<AddIcon />} sx={{ textTransform: 'none' }}
                    onClick={() => setP(prev => ({ ...prev, latePenalty: { ...prev.latePenalty, countMultiplierTiers: [...(prev.latePenalty?.countMultiplierTiers || []), { minCount: 1, maxCount: null, multiplier: 1 }] } }))}>
                    Thêm bậc
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                  Hệ số nhân lên TOÀN BỘ tiền phạt trễ của tháng, không phải từng lần.
                </Typography>
                {(p.latePenalty?.countMultiplierTiers || []).map((t, i) => {
                  const patch = (field, value) => setP(prev => ({
                    ...prev,
                    latePenalty: {
                      ...prev.latePenalty,
                      countMultiplierTiers: prev.latePenalty.countMultiplierTiers.map((x, j) => j === i ? { ...x, [field]: value } : x)
                    }
                  }));
                  return (
                    <Grid container spacing={1} alignItems="center" key={i} sx={{ mb: 0.5 }}>
                      <Grid item xs={3.5}><TextField fullWidth size="small" label="Từ (lần)" type="number" value={t.minCount} onChange={e => patch('minCount', Number(e.target.value))} /></Grid>
                      <Grid item xs={3.5}><TextField fullWidth size="small" label="Đến (trống=∞)" type="number" value={t.maxCount ?? ''} onChange={e => patch('maxCount', e.target.value === '' ? null : Number(e.target.value))} /></Grid>
                      <Grid item xs={3.5}><TextField fullWidth size="small" label="Hệ số ×" type="number" inputProps={{ step: 0.1 }} value={t.multiplier} onChange={e => patch('multiplier', Number(e.target.value))} /></Grid>
                      <Grid item xs={1.5}>
                        <IconButton size="small" color="error" onClick={() => setP(prev => ({ ...prev, latePenalty: { ...prev.latePenalty, countMultiplierTiers: prev.latePenalty.countMultiplierTiers.filter((_, j) => j !== i) } }))}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  );
                })}

                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Ngưỡng cắt thưởng &amp; cảnh cáo (0 = tắt)</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} sm={3}>
                    <Tooltip title='Trễ từ N lần → mất thưởng "không trễ ca"'>
                      <TextField fullWidth size="small" label="Mất thưởng không trễ từ" type="number" value={p.latePenalty?.cutNoLateBonusFrom ?? 0} onChange={e => setNested('latePenalty', 'cutNoLateBonusFrom', Number(e.target.value))} />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Tooltip title="Trễ từ N lần → cắt TOÀN BỘ thưởng chuyên cần (giữ thưởng mốc GMV và phụ cấp ca đêm)">
                      <TextField fullWidth size="small" label="Cắt hết chuyên cần từ" type="number" value={p.latePenalty?.cutAllAttendanceBonusesFrom ?? 0} onChange={e => setNested('latePenalty', 'cutAllAttendanceBonusesFrom', Number(e.target.value))} />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Cảnh cáo văn bản từ" type="number" value={p.latePenalty?.warningFrom ?? 0} onChange={e => setNested('latePenalty', 'warningFrom', Number(e.target.value))} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Cảnh cáo nặng từ" type="number" value={p.latePenalty?.severeWarningFrom ?? 0} onChange={e => setNested('latePenalty', 'severeWarningFrom', Number(e.target.value))} /></Grid>
                </Grid>

                {(user.role === 'leader_live' || p.template === 'lead') && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight={600}>LEAD — chế tài theo tỷ lệ trễ toàn team</Typography>
                      <Button size="small" startIcon={<AddIcon />} sx={{ textTransform: 'none' }}
                        onClick={() => setP(prev => ({ ...prev, latePenalty: { ...prev.latePenalty, teamLateRateTiers: [...(prev.latePenalty?.teamLateRateTiers || []), { minRatePct: 0, maxRatePct: null, amount: 0, requirePlan: false }] } }))}>
                        Thêm bậc
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                      Tỷ lệ trễ = (số ca team bị trễ) / (tổng số ca team trong tháng) × 100%. Ngưỡng dưới tính là &ldquo;vượt quá&rdquo;.
                    </Typography>
                    {(p.latePenalty?.teamLateRateTiers || []).map((t, i) => {
                      const patch = (field, value) => setP(prev => ({
                        ...prev,
                        latePenalty: {
                          ...prev.latePenalty,
                          teamLateRateTiers: prev.latePenalty.teamLateRateTiers.map((x, j) => j === i ? { ...x, [field]: value } : x)
                        }
                      }));
                      return (
                        <Grid container spacing={1} alignItems="center" key={i} sx={{ mb: 0.5 }}>
                          <Grid item xs={6} sm={2.5}><TextField fullWidth size="small" label="Trên (%)" type="number" value={t.minRatePct} onChange={e => patch('minRatePct', Number(e.target.value))} /></Grid>
                          <Grid item xs={6} sm={2.5}><TextField fullWidth size="small" label="Đến (%, trống=∞)" type="number" value={t.maxRatePct ?? ''} onChange={e => patch('maxRatePct', e.target.value === '' ? null : Number(e.target.value))} /></Grid>
                          <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Phạt (đ)" type="number" value={t.amount} onChange={e => patch('amount', Number(e.target.value))} /></Grid>
                          <Grid item xs={6} sm={3}><FormControlLabel control={<Switch size="small" checked={!!t.requirePlan} onChange={e => patch('requirePlan', e.target.checked)} />} label="Nộp kế hoạch" /></Grid>
                          <Grid item xs={12} sm={1}>
                            <IconButton size="small" color="error" onClick={() => setP(prev => ({ ...prev, latePenalty: { ...prev.latePenalty, teamLateRateTiers: prev.latePenalty.teamLateRateTiers.filter((_, j) => j !== i) } }))}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        </Grid>
                      );
                    })}
                  </>
                )}
              </Paper>

              {/* Thưởng chuyên cần */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <SectionTitle>Thưởng chuyên cần</SectionTitle>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Đủ giờ N tháng" type="number" value={p.attendanceBonuses?.consecutiveMonths?.months ?? 3} onChange={e => setP(prev => ({ ...prev, attendanceBonuses: { ...prev.attendanceBonuses, consecutiveMonths: { ...prev.attendanceBonuses.consecutiveMonths, months: Number(e.target.value) } } }))} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Thưởng N tháng (đ)" type="number" value={p.attendanceBonuses?.consecutiveMonths?.bonus ?? 0} onChange={e => setP(prev => ({ ...prev, attendanceBonuses: { ...prev.attendanceBonuses, consecutiveMonths: { ...prev.attendanceBonuses.consecutiveMonths, bonus: Number(e.target.value) } } }))} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Thưởng không trễ (đ)" type="number" value={p.attendanceBonuses?.noLate?.bonus ?? 0} onChange={e => setP(prev => ({ ...prev, attendanceBonuses: { ...prev.attendanceBonuses, noLate: { bonus: Number(e.target.value) } } }))} /></Grid>
                  <Grid item xs={6} sm={3} />
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Vượt giờ (%)" type="number" value={p.attendanceBonuses?.overHours?.pct ?? 10} onChange={e => setP(prev => ({ ...prev, attendanceBonuses: { ...prev.attendanceBonuses, overHours: { ...prev.attendanceBonuses.overHours, pct: Number(e.target.value) } } }))} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Thưởng vượt giờ (đ)" type="number" value={p.attendanceBonuses?.overHours?.bonus ?? 0} onChange={e => setP(prev => ({ ...prev, attendanceBonuses: { ...prev.attendanceBonuses, overHours: { ...prev.attendanceBonuses.overHours, bonus: Number(e.target.value) } } }))} /></Grid>
                </Grid>
              </Paper>

              {/* Leader */}
              {(user.role === 'leader_live' || p.template === 'lead') && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#eff6ff' }}>
                  <SectionTitle>LEAD — % hoa hồng &amp; thưởng</SectionTitle>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    Để trống = dùng số đã cấu hình ở hồ sơ nhân sự. Điền vào đây khi cần đổi % theo mốc ngày (hẹn lịch đổi).
                  </Typography>
                  <Grid container spacing={1.5} sx={{ mb: 1 }}>
                    <Grid item xs={6} sm={4}>
                      <Tooltip title="Hoa hồng trên tổng GMV team (vd 0,4)">
                        <TextField fullWidth size="small" label="Hoa hồng team (%)" type="number" inputProps={{ step: 0.1 }}
                          value={p.leader?.teamCommissionPercent ?? ''}
                          onChange={e => setP(prev => ({ ...prev, leader: { ...prev.leader, teamCommissionPercent: e.target.value === '' ? null : Number(e.target.value) } }))} />
                      </Tooltip>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField fullWidth size="small" label="Thưởng tăng trưởng (%)" type="number" inputProps={{ step: 0.1 }}
                        value={p.leader?.growthBonusPercent ?? ''}
                        onChange={e => setP(prev => ({ ...prev, leader: { ...prev.leader, growthBonusPercent: e.target.value === '' ? null : Number(e.target.value) } }))} />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField fullWidth size="small" label="KPI TikTok (%)" type="number" inputProps={{ step: 0.1 }}
                        value={p.leader?.tiktokKpiBonusPercent ?? ''}
                        onChange={e => setP(prev => ({ ...prev, leader: { ...prev.leader, tiktokKpiBonusPercent: e.target.value === '' ? null : Number(e.target.value) } }))} />
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight={600}>Thưởng theo tỷ trọng TikTok</Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={() => setP(prev => ({ ...prev, leader: { ...prev.leader, tiktokRatioBonusTiers: [...(prev.leader?.tiktokRatioBonusTiers || []), { minRatioPct: 20, bonus: 0 }] } }))} sx={{ textTransform: 'none' }}>Thêm bậc</Button>
                  </Stack>
                  {(p.leader?.tiktokRatioBonusTiers || []).map((t, i) => (
                    <Grid container spacing={1} alignItems="center" key={i} sx={{ mb: 0.5 }}>
                      <Grid item xs={5}><TextField fullWidth size="small" label="Tỷ trọng ≥ (%)" type="number" value={t.minRatioPct} onChange={e => setP(prev => ({ ...prev, leader: { ...prev.leader, tiktokRatioBonusTiers: prev.leader.tiktokRatioBonusTiers.map((x, j) => j === i ? { ...x, minRatioPct: Number(e.target.value) } : x) } }))} /></Grid>
                      <Grid item xs={5}><TextField fullWidth size="small" label="Thưởng (đ)" type="number" value={t.bonus} onChange={e => setP(prev => ({ ...prev, leader: { ...prev.leader, tiktokRatioBonusTiers: prev.leader.tiktokRatioBonusTiers.map((x, j) => j === i ? { ...x, bonus: Number(e.target.value) } : x) } }))} /></Grid>
                      <Grid item xs={2}><IconButton size="small" color="error" onClick={() => setP(prev => ({ ...prev, leader: { ...prev.leader, tiktokRatioBonusTiers: prev.leader.tiktokRatioBonusTiers.filter((_, j) => j !== i) } }))}><DeleteIcon fontSize="small" /></IconButton></Grid>
                    </Grid>
                  ))}
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Chế tài tỷ trọng TikTok thấp</Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={4}><TextField fullWidth size="small" label="Ngưỡng < (%)" type="number" value={p.leader?.tiktokRatioPenalty?.thresholdPct ?? 15} onChange={e => setP(prev => ({ ...prev, leader: { ...prev.leader, tiktokRatioPenalty: { ...prev.leader.tiktokRatioPenalty, thresholdPct: Number(e.target.value) } } }))} /></Grid>
                    <Grid item xs={4}><TextField fullWidth size="small" label="Số tháng liên tiếp" type="number" value={p.leader?.tiktokRatioPenalty?.months ?? 2} onChange={e => setP(prev => ({ ...prev, leader: { ...prev.leader, tiktokRatioPenalty: { ...prev.leader.tiktokRatioPenalty, months: Number(e.target.value) } } }))} /></Grid>
                    <Grid item xs={4}><TextField fullWidth size="small" label="Phạt (đ)" type="number" value={p.leader?.tiktokRatioPenalty?.amount ?? 500000} onChange={e => setP(prev => ({ ...prev, leader: { ...prev.leader, tiktokRatioPenalty: { ...prev.leader.tiktokRatioPenalty, amount: Number(e.target.value) } } }))} /></Grid>
                  </Grid>
                </Paper>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'none', borderRadius: 2 }}>Đóng</Button>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={handleSave} disabled={saving || !p} sx={{ textTransform: 'none', borderRadius: 2 }}>Lưu chính sách</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={noti.open} autoHideDuration={3000} onClose={() => setNoti(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={noti.severity} variant="filled" sx={{ borderRadius: 2 }}>{noti.message}</Alert>
      </Snackbar>
    </>
  );
};

export default LivePolicyEditor;
